/**
 * useIncidentsConfig.js
 *
 * Hook for managing vessel-specific incident reporting configuration settings.
 * WBC3 / MAIB Reporting Requirements
 *
 * Settings:
 * 1. Default Severity - Initial severity for new incidents
 * 2. Investigation Required Threshold - Severity level requiring formal investigation
 * 3. Auto-close When Actions Complete - Auto-close when all remedial actions done
 * 4. Require Investigation Report - WBC3 compliance for high-severity incidents
 * 5. Show Closed Incidents - Toggle visibility of closed incidents in main list
 * 6. MAIB Notification Reminder - Show reminder for reportable incidents
 * 7. Corrective Action Target Days - Target days to complete corrective actions
 *
 * ANCHOR MODE compliant - Domain Spine v2
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
// import { useVesselCollections } from '../../useVesselCollections'; // Phase 2.6: Disabled for IndexedDB migration
import { logCRUDOperation, AUDIT_ACTIONS } from '../../../services/auditTrail';

// RBAC helper (same pattern as other config hooks)
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch {
    return false;
  }
};

/**
 * Severity options for incidents
 */
export const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low', description: 'Minor issue, no injuries or damage' },
  { value: 'medium', label: 'Medium', description: 'Notable incident, minor injuries/damage' },
  { value: 'high', label: 'High', description: 'Serious incident, injuries or significant damage' },
  { value: 'critical', label: 'Critical', description: 'Major incident, severe injuries/casualties' }
];

/**
 * Default incidents configuration
 */
const getDefaultConfig = () => ({
  version: 1,
  revision: 1,

  // Setting 1: Default severity for new incidents
  defaultSeverity: 'medium',

  // Setting 2: Severity level that requires formal investigation
  investigationRequiredThreshold: 'medium',

  // Setting 3: Auto-close when all corrective actions complete
  autoCloseWhenActionsComplete: true,

  // Setting 4: Require formal investigation report (WBC3 compliance)
  requireInvestigationReport: true,

  // Setting 5: Show closed incidents in main list
  showClosedIncidents: false,

  // Setting 6: Show MAIB reporting reminder for serious incidents
  maibNotificationReminder: true,

  // Setting 7: Target days to complete corrective actions
  correctiveActionTargetDays: 30,

  // Metadata
  lastModified: new Date().toISOString(),
  modifiedBy: { name: 'System', userId: 'system' }
});

/**
 * Hook for managing incidents configuration
 */
export const useIncidentsConfig = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // const { collections } = useVesselCollections(); // Phase 2.6: Disabled for IndexedDB migration

  // Vessel-scoped storage
  const storageKey = 'incidents_config';
  const { value: rawConfig, setValue: setConfig } = useStorageState(
    storageKey,
    getDefaultConfig()
  );

  // Ensure config has all required fields (handle upgrades)
  const config = useMemo(() => {
    const defaults = getDefaultConfig();
    return {
      ...defaults,
      ...rawConfig
    };
  }, [rawConfig]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Check if any regulatory settings are disabled
   */
  const disabledRegulatorySettings = useMemo(() => {
    const disabled = [];
    if (!config.requireInvestigationReport) {
      disabled.push('Require Investigation Report');
    }
    if (!config.maibNotificationReminder) {
      disabled.push('MAIB Notification Reminder');
    }
    return disabled;
  }, [config.requireInvestigationReport, config.maibNotificationReminder]);

  /**
   * Get severity level details
   */
  const getSeverityDetails = useCallback((severity) => {
    return SEVERITY_OPTIONS.find(opt => opt.value === severity) || SEVERITY_OPTIONS[1];
  }, []);

  /**
   * Check if a severity level requires investigation based on threshold
   */
  const requiresInvestigation = useCallback((severity) => {
    const severityOrder = ['low', 'medium', 'high', 'critical'];
    const incidentLevel = severityOrder.indexOf(severity);
    const thresholdLevel = severityOrder.indexOf(config.investigationRequiredThreshold);
    return incidentLevel >= thresholdLevel;
  }, [config.investigationRequiredThreshold]);

  // ============================================================================
  // MUTATIONS WITH AUDIT TRAIL
  // ============================================================================

  /**
   * Update a single setting
   */
  const updateSetting = useCallback(async (settingKey, value) => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useIncidentsConfig] Permission denied');
      return false;
    }

    const oldValue = config[settingKey];

    const updatedConfig = {
      ...config,
      revision: (config.revision || 0) + 1,
      [settingKey]: value,
      lastModified: new Date().toISOString(),
      modifiedBy: { name: currentUser?.name, userId: currentUser?.id }
    };

    await setConfig(updatedConfig);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'incidents_config',
    //     {
    //       vesselId: currentVessel?.id,
    //       setting: settingKey,
    //       newValue: value,
    //       oldValue
    //     },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useIncidentsConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  /**
   * Set default severity
   */
  const setDefaultSeverity = useCallback(async (severity) => {
    const valid = SEVERITY_OPTIONS.some(opt => opt.value === severity);
    if (!valid) {
      console.warn('[useIncidentsConfig] Invalid severity:', severity);
      return false;
    }
    return updateSetting('defaultSeverity', severity);
  }, [updateSetting]);

  /**
   * Set investigation required threshold
   */
  const setInvestigationRequiredThreshold = useCallback(async (severity) => {
    const valid = SEVERITY_OPTIONS.some(opt => opt.value === severity);
    if (!valid) {
      console.warn('[useIncidentsConfig] Invalid severity threshold:', severity);
      return false;
    }
    return updateSetting('investigationRequiredThreshold', severity);
  }, [updateSetting]);

  /**
   * Toggle auto-close when actions complete
   */
  const setAutoCloseWhenActionsComplete = useCallback(async (enabled) => {
    return updateSetting('autoCloseWhenActionsComplete', !!enabled);
  }, [updateSetting]);

  /**
   * Toggle require investigation report
   */
  const setRequireInvestigationReport = useCallback(async (require) => {
    return updateSetting('requireInvestigationReport', !!require);
  }, [updateSetting]);

  /**
   * Toggle show closed incidents
   */
  const setShowClosedIncidents = useCallback(async (show) => {
    return updateSetting('showClosedIncidents', !!show);
  }, [updateSetting]);

  /**
   * Toggle MAIB notification reminder
   */
  const setMaibNotificationReminder = useCallback(async (show) => {
    return updateSetting('maibNotificationReminder', !!show);
  }, [updateSetting]);

  /**
   * Set corrective action target days
   */
  const setCorrectiveActionTargetDays = useCallback(async (days) => {
    const clamped = Math.min(365, Math.max(7, days));
    return updateSetting('correctiveActionTargetDays', clamped);
  }, [updateSetting]);

  /**
   * Reset to defaults
   */
  const resetToDefaults = useCallback(async () => {
    if (!canDo(can, 'settings', 'update')) {
      console.warn('[useIncidentsConfig] Permission denied');
      return false;
    }

    const defaults = getDefaultConfig();
    defaults.revision = (config.revision || 0) + 1;
    defaults.lastModified = new Date().toISOString();
    defaults.modifiedBy = { name: currentUser?.name, userId: currentUser?.id };

    await setConfig(defaults);

    // Log to audit trail
    // Phase 2.6: Audit temporarily disabled for IndexedDB migration
    // try {
    //   await logCRUDOperation(
    //     collections,
    //     AUDIT_ACTIONS.UPDATE,
    //     'incidents_config',
    //     { vesselId: currentVessel?.id, action: 'reset_to_defaults' },
    //     { userId: currentUser?.id, name: currentUser?.name }
    //   );
    // } catch (e) {
    //   console.warn('[useIncidentsConfig] Audit log failed:', e);
    // }

    return true;
  }, [can, config, setConfig, currentUser, currentVessel]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Raw config
    config,

    // Computed values
    disabledRegulatorySettings,
    getSeverityDetails,
    requiresInvestigation,

    // Direct settings access
    defaultSeverity: config.defaultSeverity,
    investigationRequiredThreshold: config.investigationRequiredThreshold,
    autoCloseWhenActionsComplete: config.autoCloseWhenActionsComplete,
    requireInvestigationReport: config.requireInvestigationReport,
    showClosedIncidents: config.showClosedIncidents,
    maibNotificationReminder: config.maibNotificationReminder,
    correctiveActionTargetDays: config.correctiveActionTargetDays,

    // Individual mutations
    setDefaultSeverity,
    setInvestigationRequiredThreshold,
    setAutoCloseWhenActionsComplete,
    setRequireInvestigationReport,
    setShowClosedIncidents,
    setMaibNotificationReminder,
    setCorrectiveActionTargetDays,
    resetToDefaults,

    // Generic update
    updateSetting,

    // Metadata
    lastModified: config.lastModified,
    modifiedBy: config.modifiedBy,
    revision: config.revision,

    // Constants for UI
    SEVERITY_OPTIONS
  };
};

export default useIncidentsConfig;
